import { useCallback, useEffect, useState } from 'react'
import type { Recaptcha, RecaptchaAction } from './recaptcha'
import { useFillTime } from './useFillTime'

/** API.md §1.12 `BotInput` as the lookup and booking send it. */
export interface BotInputs {
  scoreToken: string | null
  /** P-13's checkbox token, on the resubmit after `challenge_required` only. */
  challengeToken?: string
  hp: string
  fillMs: number
}

/**
 * The bot inputs of one form (Frontend §4.6): the honeypot's value (render `<Honeypot {...bot.honeypot} />` inside
 * the form), the fill time from the form's first render, and a fresh reCAPTCHA score token for `action`, asked for
 * right before the submit (tokens are single-use and last 2 minutes). Showing the form starts loading the script. The
 * client never decides anything and never blocks the submit: no token within 3 s → `scoreToken: null`.
 */
export function useBotInputs(
  recaptcha: Recaptcha,
  action: RecaptchaAction,
): {
  honeypot: { value: string; onChange: (value: string) => void }
  collect: (challengeToken?: string) => Promise<BotInputs>
} {
  const [hp, setHp] = useState('')
  const fillMs = useFillTime()
  useEffect(() => {
    recaptcha.load()
  }, [recaptcha])
  const collect = useCallback(
    async (challengeToken?: string): Promise<BotInputs> => {
      const filled = fillMs()
      const scoreToken = await recaptcha.executeScore(action)
      return { scoreToken, ...(challengeToken ? { challengeToken } : {}), hp, fillMs: filled }
    },
    [recaptcha, action, hp, fillMs],
  )
  return { honeypot: { value: hp, onChange: setHp }, collect }
}
